/**
 * Shared filtering, sorting, and utility functions for job cards
 * Used by JobCardsScheduler and JobCardsWerker components
 */

export type PaymentFilter = "all" | "paid" | "unpaid";
export type StatusFilter = "all" | "cancelled" | "not_cancelled";
export type SortMode = "smart" | "date_newest" | "date_oldest";

export interface JobCardFilters {
  payment?: PaymentFilter;
  status?: StatusFilter;
  sort?: SortMode;
}

// ============================================================================
// Date/Time Utilities
// ============================================================================

/**
 * Get start timestamp from job data (handles various field names)
 * Falls back to CreatedAt if start date is invalid
 */
export const getStartTs = (job: any): number => {
  const raw = job?.StartDateTimeRaw ?? job?.startDateTimeRaw ?? job?.StartDateTime;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : typeof job.CreatedAt === "number" ? job.CreatedAt : 0;
};

/**
 * Get finish timestamp from job data (handles various field names)
 */
export const getFinishTs = (job: any): number => {
  const raw = job?.FinishDateTimeRaw ?? job?.finishDateTimeRaw ?? job?.FinishDateTime;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
};

/**
 * Get created timestamp from job data
 */
export const getCreatedTs = (job: any): number => {
  const t = typeof job.CreatedAt === "number" ? job.CreatedAt : 0;
  return Number.isFinite(t) ? t : 0;
};

/**
 * Calculate hours duration between start and finish times
 * Returns formatted string like "5.5 hrs" or empty string if invalid
 */
export const getHoursText = (job: any): string => {
  const s = getStartTs(job);
  const f = getFinishTs(job);
  if (!s || !Number.isFinite(f) || f <= s) return "";
  const hours = Math.round(((f - s) / 36e5) * 10) / 10; // 1 decimal
  if (!Number.isFinite(hours) || hours <= 0) return "";
  return `${hours} hrs`;
};

// ============================================================================
// Filter Functions
// ============================================================================

/**
 * Filter jobs by payment status (for scheduler view)
 * @param isPastSegment - Only apply filter in Past segment
 */
export const createPaymentFilter = (filterValue: PaymentFilter, isPastSegment: boolean) => {
  return (job: any): boolean => {
    if (!isPastSegment) return true; // Only filter in Past segment
    if (filterValue === "all") return true;
    if (filterValue === "paid") return job.PaidWerkers === true;
    return job.PaidWerkers === false; // unpaid
  };
};

/**
 * Filter jobs by payment status (for werker view)
 * @param allowChips - Only apply filter when chips are allowed (Past segment)
 */
export const createWerkerPaymentFilter = (filterValue: PaymentFilter, allowChips: boolean) => {
  return (job: any): boolean => {
    if (!allowChips) return true;
    if (filterValue === "all") return true;
    const isPaid = job.PaidWerker === true;
    if (filterValue === "paid") return isPaid;
    if (filterValue === "unpaid") return !isPaid;
    return true;
  };
};

/**
 * Filter jobs by cancellation status (for scheduler view)
 * @param isPastSegment - Only apply filter in Past segment
 */
export const createStatusFilter = (filterValue: StatusFilter, isPastSegment: boolean) => {
  return (job: any): boolean => {
    if (!isPastSegment) return true; // Only filter in Past segment
    if (filterValue === "all") return true;
    if (filterValue === "cancelled") return job.JobCancelledByScheduler === true;
    return job.JobCancelledByScheduler === false; // not_cancelled
  };
};

/**
 * Filter jobs by cancellation status (for werker view)
 * @param allowChips - Only apply filter when chips are allowed (Past segment)
 */
export const createWerkerStatusFilter = (filterValue: StatusFilter, allowChips: boolean) => {
  return (job: any): boolean => {
    if (!allowChips) return true;
    if (filterValue === "all") return true;
    const feedStatus = (job as any).__feedStatus;
    const isCancelled = job.JobCancelledByScheduler === true || feedStatus === "cancelled";
    if (filterValue === "cancelled") return isCancelled;
    if (filterValue === "not_cancelled") return !isCancelled;
    return true;
  };
};

/**
 * Create text search filter for scheduler job cards
 * Searches: Location, ShiftIdentifier, Company
 */
export const createSchedulerSearchFilter = (searchText: string) => {
  return (job: any): boolean => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      job.Location?.toLowerCase().includes(q) ||
      job.ShiftIdentifier?.toLowerCase().includes(q) ||
      job.Company?.toLowerCase().includes(q)
    );
  };
};

/**
 * Create text search filter for werker job cards
 * Searches: Location, ShiftIdentifier, Company, SchedFirstName, SchedLastName
 */
export const createWerkerSearchFilter = (searchText: string) => {
  return (job: any): boolean => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      job.Location?.toLowerCase().includes(q) ||
      job.location?.toLowerCase().includes(q) ||
      job.ShiftIdentifier?.toLowerCase().includes(q) ||
      job.shiftIdentifier?.toLowerCase().includes(q) ||
      job.Company?.toLowerCase().includes(q) ||
      job.company?.toLowerCase().includes(q) ||
      job.SchedFirstName?.toLowerCase().includes(q) ||
      job.schedFirstName?.toLowerCase().includes(q) ||
      job.SchedLastName?.toLowerCase().includes(q) ||
      job.schedLastName?.toLowerCase().includes(q)
    );
  };
};

// ============================================================================
// Sort Functions
// ============================================================================

/**
 * Sort jobs by date (newest or oldest first)
 */
export const sortByDate = (jobs: any[], mode: "date_newest" | "date_oldest"): any[] => {
  return [...jobs].sort((a: any, b: any) => {
    const dateA = getStartTs(a);
    const dateB = getStartTs(b);
    return mode === "date_newest" ? dateB - dateA : dateA - dateB;
  });
};

/**
 * Sort jobs using "smart" mode (context-dependent)
 * @param isPastSegment - If true, newest first; if false, soonest first
 */
export const sortSmart = (jobs: any[], isPastSegment: boolean): any[] => {
  return [...jobs].sort((a: any, b: any) => {
    const aTs = getStartTs(a);
    const bTs = getStartTs(b);
    // Past: newest first, Scheduled/Future: soonest first
    return isPastSegment ? bTs - aTs : aTs - bTs;
  });
};

/**
 * Apply sort mode to job list
 */
export const applySortMode = (
  jobs: any[],
  sortMode: SortMode,
  isPastSegment: boolean
): any[] => {
  if (sortMode === "date_newest" || sortMode === "date_oldest") {
    return sortByDate(jobs, sortMode);
  }
  // smart mode
  return sortSmart(jobs, isPastSegment);
};

// ============================================================================
// Scheduler Past Jobs Filter (checkbox-based)
// ============================================================================

export interface PastJobFilters {
  werkersPaid: boolean;
  werkersUnpaid: boolean;
  jobPaid: boolean;
  jobUnpaid: boolean;
  jobCancelled: boolean;
}

export const DEFAULT_PAST_FILTERS: PastJobFilters = {
  werkersPaid: true,
  werkersUnpaid: true,
  jobPaid: true,
  jobUnpaid: true,
  jobCancelled: false,
};

/**
 * Filter scheduler past jobs using the checkbox-based filter panel.
 * Only active when isPastSegment is true.
 */
export const createPastJobsFilter = (filters: PastJobFilters, isPastSegment: boolean) => {
  return (job: any): boolean => {
    if (!isPastSegment) return true;

    // Hide cancelled jobs unless jobCancelled is explicitly checked
    if (!filters.jobCancelled && job.JobCancelledByScheduler === true) return false;

    // Flat OR: show job if it matches any checked filter
    if (filters.werkersPaid && job.PaidWerkers === true) return true;
    if (filters.werkersUnpaid && job.PaidWerkers !== true) return true;
    if (filters.jobPaid && job.IsPaidJob === true) return true;
    if (filters.jobUnpaid && job.IsPaidJob !== true) return true;
    if (filters.jobCancelled && job.JobCancelledByScheduler === true) return true;

    return false;
  };
};
